/**
 * S0 assistive IFU freeze — locks product intended-use / excluded-use strings
 * to nervedx-qms docs/regulatory/EEG_ASSISTIVE_IFU_LANGUAGE_FREEZE_2026-09-12.md
 *
 * Full frozen text remains the regulatory source of truth (Manual / Labeling
 * disclosure). Compact UI summaries below are short in-app chrome only and
 * must stay consistent with the freeze (non-diagnostic / assistive framing).
 */
export const ASSISTIVE_IFU_FREEZE_ID =
  "NDX-REG-EEG-ASSISTIVE-IFU-FREEZE-2026-09-12";

export const ASSISTIVE_IFU_FREEZE_DATE = "2026-09-12";

/** Must match QMS IFU freeze §2.1 and AI_TRIAGE_INTENDED_USE. */
export const FROZEN_AI_TRIAGE_INTENDED_USE =
  "AI triage candidates are non-diagnostic decision-support aids that help prioritize human review of raw EEG. They do not provide standalone clinical interpretation or diagnosis.";

export const FROZEN_AI_TRIAGE_EXCLUDED_USE =
  "Excluded use: autonomous diagnosis, unattended alarming, closed-loop therapy, and any clinical action without qualified waveform review. Release phase remains regulatory Phase 3 gated until cleared.";

/** Must match QMS IFU freeze §2.2 and QEEG_INTENDED_USE_SUMMARY. */
export const FROZEN_QEEG_INTENDED_USE_SUMMARY =
  "qEEG trends are non-diagnostic decision-support visualization aids for navigating raw EEG. They do not provide standalone clinical interpretation.";

export const FROZEN_QEEG_EXCLUDED_USE_SUMMARY =
  "Excluded use: autonomous diagnosis, closed-loop therapy control, and unattended alarming without clinician waveform review.";

/** Must match QMS IFU freeze §2.3 and ARTIFACT_QC_INTENDED_USE. */
export const FROZEN_ARTIFACT_QC_INTENDED_USE =
  "Artifact markers, impedance timelines, and selective artifact-reduction views are technologist and clinician decision-support aids for signal-quality review. They do not replace raw EEG review or diagnostic interpretation.";

export const FROZEN_ARTIFACT_QC_EXCLUDED_USE =
  "Excluded use: permanent overwrite of original EEG samples, unsupervised clinical decision-making from reduced views, and QC sign-off by non-qualified roles.";

/** Must match QMS IFU freeze §2.5 and SEIZURE_BURDEN_NCSE_INTENDED_USE (CX-G04). */
export const FROZEN_SEIZURE_BURDEN_NCSE_INTENDED_USE =
  "Five-minute seizure-burden trends and suspected NCSE/ESE alerts are non-diagnostic decision-support aids that help prioritize human review of raw EEG during monitoring. They do not diagnose status epilepticus or prescribe treatment.";

export const FROZEN_SEIZURE_BURDEN_NCSE_EXCLUDED_USE =
  "Excluded use: autonomous diagnosis of NCSE/ESE, unattended treatment decisions, closed-loop therapy, and clinical action without qualified waveform review.";

/** Compact always-visible chrome — not a substitute for the frozen IFU. */
export const AI_TRIAGE_IFU_UI_BADGE =
  "Non-diagnostic decision support · Phase 3 gated";

export const AI_TRIAGE_IFU_UI_SUMMARY =
  "Decision-support aid that prioritizes human review of raw EEG — not for standalone diagnosis.";

export const QEEG_IFU_UI_BADGE = "Non-diagnostic";

export const QEEG_IFU_UI_SUMMARY =
  "Decision-support visualization for navigating raw EEG — not for standalone clinical interpretation.";

export const ARTIFACT_QC_IFU_UI_BADGE = "Assistive QC";

export const ARTIFACT_QC_IFU_UI_SUMMARY =
  "Decision-support for signal-quality review — does not replace raw EEG review or diagnostic interpretation.";

export const SEIZURE_BURDEN_NCSE_IFU_UI_BADGE =
  "Assistive 5‑min burden · Non-diagnostic";

export const SEIZURE_BURDEN_NCSE_IFU_UI_SUMMARY =
  "5‑minute seizure burden and NCSE/ESE alerts prioritize waveform review — not a diagnosis or treatment order.";

export const SEIZURE_BURDEN_NCSE_INTENDED_USE =
  FROZEN_SEIZURE_BURDEN_NCSE_INTENDED_USE;
export const SEIZURE_BURDEN_NCSE_EXCLUDED_USE =
  FROZEN_SEIZURE_BURDEN_NCSE_EXCLUDED_USE;

/** Must match QMS IFU freeze §2.6 (CX-G07 iEEG). */
export const FROZEN_IEEG_INTENDED_USE =
  "iEEG electrode localization, specialty montages, and contact-group trends are non-diagnostic decision-support aids for intracranial EEG review. They do not define surgical targets or replace raw waveform review.";

export const FROZEN_IEEG_EXCLUDED_USE =
  "Excluded use: surgical planning from localization alone, autonomous resection guidance, and clinical action without qualified intracranial EEG review.";

export const IEEG_IFU_UI_BADGE = "Assistive iEEG · Non-diagnostic";

export const IEEG_IFU_UI_SUMMARY =
  "Contact localization and iEEG montages/trends aid review — not surgical targeting.";

/** Must match QMS IFU freeze §2.7 (CX-G08 normative z-maps). */
export const FROZEN_NORMATIVE_ZMAP_INTENDED_USE =
  "Age-matched normative z-score brain maps are non-diagnostic decision-support visualizations that compare band power to a referenced normative table. Always verify on raw EEG.";

export const FROZEN_NORMATIVE_ZMAP_EXCLUDED_USE =
  "Excluded use: standalone diagnosis from z-maps, population labeling of individuals, and clinical conclusions without normative provenance review and raw EEG confirmation.";

export const NORMATIVE_ZMAP_IFU_UI_BADGE = "Assistive z-map · Non-diagnostic";

export const NORMATIVE_ZMAP_IFU_UI_SUMMARY =
  "Normative z-score maps are adjunct visualizations — confirm findings on raw EEG.";

/** Must match QMS IFU freeze §2.8 (CX-G09 vsBaseline). */
export const FROZEN_VSBASELINE_INTENDED_USE =
  "vsBaseline long-horizon qEEG trends show change from a clinician-selected baseline epoch as decision-support navigation aids. They do not provide standalone clinical interpretation.";

export const FROZEN_VSBASELINE_EXCLUDED_USE =
  "Excluded use: autonomous alarming from baseline deviation, closed-loop therapy, and clinical action without raw EEG review.";

export const VSBASELINE_IFU_UI_BADGE = "Assistive vsBaseline";

export const VSBASELINE_IFU_UI_SUMMARY =
  "Change-from-baseline trends help navigate long studies — not diagnostic conclusions.";
